import { DEFAULT_INK_WIDTH } from '@src/utility/annotationInk';
import { useCallback, useMemo, useState } from 'react';

import type { Stroke, StrokeTool } from '@src/types.ts/comment';

export interface ClipAnnotations {
  /** Armed tool, or `null` when the canvas is left alone. */
  tool: StrokeTool | null;
  setTool: (tool: StrokeTool | null) => void;
  color: string;
  setColor: (color: string) => void;
  width: number;
  strokes: Stroke[];
  setStrokes: (strokes: Stroke[]) => void;
  /** True while there is anything to save or discard. */
  hasMarks: boolean;
  /**
   * Whether any ink paints on the frame at all. Authoritative: when false
   * nothing renders, in-progress marks included, because a control labelled
   * "Hide Drawings" that left some drawings visible would be lying.
   */
  drawingsVisible: boolean;
  setDrawingsVisible: (visible: boolean) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** Drop every mark and disarm — used on close, cancel, and after posting. */
  reset: () => void;
}

/**
 * Owns the in-progress drawing for the open clip (PLAT-11288).
 *
 * Deliberately **component state, not the comment store**: an unposted drawing
 * is not session state anyone else reads, and it is discarded if the reviewer
 * closes the clip. It becomes store data only when it is saved with a comment
 * (PLAT-11290).
 *
 * Undo/redo is a local stroke-array history for the same reason. It is not an
 * editor command and must never reach the editor's undo stack — nothing here
 * touches the timeline, and putting "undo my pencil line" between two timeline
 * edits would be a surprising thing to hand the user.
 *
 * @param defaultColor ink to start from — the author's own comment colour, so a
 * reviewer's marks match their comment markers without configuring anything.
 */
export function useClipAnnotations(defaultColor: string): ClipAnnotations {
  const [tool, setTool] = useState<StrokeTool | null>(null);
  const [drawingsVisible, setDrawingsVisible] = useState(true);
  const [color, setColor] = useState(defaultColor);
  // `past` holds every committed state; `future` holds what undo took away.
  const [past, setPast] = useState<Stroke[][]>([[]]);
  const [future, setFuture] = useState<Stroke[][]>([]);

  const strokes = past[past.length - 1];

  const setStrokes = useCallback((next: Stroke[]) => {
    setPast(history => [...history, next]);
    // A new mark after an undo abandons the redone future, as every editor does.
    setFuture([]);
  }, []);

  const undo = useCallback(() => {
    setPast(history => {
      // Never pop the initial empty state — that is the floor, not a step.
      if (history.length <= 1) return history;
      setFuture(f => [history[history.length - 1], ...f]);
      return history.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture(f => {
      if (f.length === 0) return f;
      setPast(history => [...history, f[0]]);
      return f.slice(1);
    });
  }, []);

  const reset = useCallback(() => {
    setPast([[]]);
    setFuture([]);
    setTool(null);
  }, []);

  return useMemo(
    () => ({
      tool,
      setTool,
      color,
      setColor,
      width: DEFAULT_INK_WIDTH,
      strokes,
      setStrokes,
      hasMarks: strokes.length > 0,
      drawingsVisible,
      setDrawingsVisible,
      undo,
      redo,
      canUndo: past.length > 1,
      canRedo: future.length > 0,
      reset,
    }),
    [
      tool,
      color,
      drawingsVisible,
      strokes,
      setStrokes,
      undo,
      redo,
      past.length,
      future.length,
      reset,
    ],
  );
}
